<script>
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import { CHART_COLORS } from '@/javascript/helpers/chartColors';

export default {
  name: "RecentDonationsCard",
  props: {
    donations: { type: Array, default: () => [] },
    loaded: { type: Boolean, default: false },
    error: { type: Boolean, default: false }
  },
  emits: ['select'],
  methods: {
    formattedMoney(val) {
      if (!val) return "-";
      return ApplicationHelper.formatMoney(val, true);
    },
    timeAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return '1d ago';
      if (diffDays < 30) return `${diffDays}d ago`;
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    fullDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
  }
};
</script>

<template>
  <div class="recent-content">
    <div
      v-if="error"
      class="fail-state"
    >
      <i class="fa-solid fa-triangle-exclamation text-amber-400" />
      <p>Unable to load</p>
    </div>

    <template v-else-if="!loaded">
      <div
        v-for="i in 8"
        :key="i"
        class="donation-row-skel"
      >
        <div class="skel skel-amt" />
        <div class="skel skel-fund" />
        <div class="skel skel-time" />
      </div>
    </template>

    <div
      v-else-if="donations.length === 0"
      class="fail-state"
    >
      <i class="fa-solid fa-inbox text-gray-300" />
      <p>No recent donations</p>
    </div>

    <div
      v-else
      class="recent-scroll"
    >
      <div
        v-for="donation in donations"
        :key="donation.donation_id"
        class="donation-row cursor-pointer hover:bg-gray-50 transition-colors"
        :title="`${formattedMoney(donation.amount)} → ${donation.designation} (${donation.designation_type})\n${fullDate(donation.time_ago)}`"
        @click="$emit('select', donation.donation_id)"
      >
        <span class="don-amount">{{ formattedMoney(donation.amount) }}</span>
        <span class="don-fund">{{ donation.designation }}</span>
        <span class="don-type">{{ donation.designation_type }}</span>
        <span class="don-time">{{ timeAgo(donation.time_ago) }}</span>
        <i class="fa-solid fa-chevron-right don-arrow" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.recent-content { display: flex; flex-direction: column; flex: 1; overflow: hidden; }

.fail-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  flex: 1; color: #9ca3af; font-size: 0.8rem; gap: 4px;
}

.recent-scroll {
  overflow-y: auto;
  flex: 1;
  max-height: 100%;

  /* Subtle scrollbar */
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 2px; }
  &::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
}

.donation-row {
  display: flex; align-items: center; padding: 6px 0;
  border-bottom: 1px solid rgba(8, 18, 37, 0.08);
  &:last-child { border-bottom: none; }
}

.don-amount { font-size: 0.85rem; font-weight: 600; color: #10B981; min-width: 70px; }
.don-fund { flex: 1; font-size: 0.8rem; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 0 8px; max-width: 550px; }
.don-type { font-size: 0.6rem; color: #9ca3af; text-transform: capitalize; width: 60px; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;}
.don-time { font-size: 0.7rem; color: #9ca3af; white-space: nowrap; min-width: 50px; text-align: right; margin-left: auto; }
.don-arrow { font-size: 0.6rem; color: #d1d5db; margin-left: 6px; }

.donation-row-skel { display: flex; align-items: center; gap: 8px; padding: 8px 0; }
.skel { height: 12px; background: #f3f4f6; border-radius: 3px; animation: shimmer 1.5s ease-in-out infinite; }
.skel-amt { width: 55px; }
.skel-fund { flex: 1; }
.skel-time { width: 40px; }
@keyframes shimmer { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

@media screen and (max-width: 1023px) {
  .don-type { display: none; }
}

@media print {
  .recent-content {
    overflow: visible !important;
  }
  .recent-scroll {
    overflow: visible !important;
    max-height: none !important;
  }
}
</style>
